import Dropdown from 'bootstrap/js/src/dropdown';
import { Controller } from 'stimulus';

const PADDING = 8;
const BELOW_CLASS = 'amba-dropdown-nested--below';

/**
 * Nested dropdown controller for overflow navigation.
 * A nested dropdown would by default prioritise sitting flush with it's parent over
 * not overflowing the viewport.
 *
 * This keeps dropdowns within viewport by sliding horizontally and shifting
 * below the toggle when necessary, rather than overflowing the viewport or flipping it's
 * placement (for example from left to right).
 *
 * It also adds a class on the triggering element so the chevron can be re-positioned.
 *
 * Currently only works with horizontally positioned nested dropdowns.
 */
export default class extends Controller {
  static targets = ['toggle'];

  connect() {
    // Exit early if no toggle target exists (not all overflow navs have nested dropdowns)
    if (!this.hasToggleTarget) return;

    // Prevent Bootstrap's auto-initialization so we control the dropdown
    this.toggleTarget.removeAttribute('data-bs-toggle');

    this.dropdown = new Dropdown(this.toggleTarget, {
      popperConfig: (config) => ({
        ...config,
        modifiers: [
          ...config.modifiers,
          // Disable default positioning - we handle it in keepInViewport
          { name: 'flip', enabled: false },
          { name: 'preventOverflow', enabled: false },
          {
            name: 'keepInViewport',
            enabled: true,
            phase: 'beforeWrite',
            requires: ['computeStyles'],
            fn: this.#adjustPosition,
          },
        ],
      }),
    });

    this.toggleTarget.addEventListener('click', this.#toggle);
    this.toggleTarget.addEventListener(
      'hidden.bs.dropdown',
      this.#clearBelowClass
    );
    // Close when another dropdown opens anywhere on the page
    document.addEventListener('show.bs.dropdown', this.#hideIfOther);
  }

  disconnect() {
    if (!this.hasToggleTarget) return;

    this.dropdown?.hide();
    this.toggleTarget.removeEventListener('click', this.#toggle);
    this.toggleTarget.removeEventListener(
      'hidden.bs.dropdown',
      this.#clearBelowClass
    );
    document.removeEventListener('show.bs.dropdown', this.#hideIfOther);
    this.dropdown?.dispose();
  }

  #adjustPosition = ({ state }) => {
    const offsets = state.modifiersData.popperOffsets;
    if (!offsets) return;

    const { popper, reference } = state.elements;
    const popperWidth = popper.offsetWidth;
    const refRect = reference.getBoundingClientRect();
    // How far the dropdown would overflow past the left edge of the viewport
    const overflow = PADDING - (refRect.left - popperWidth);

    if (overflow > 0) {
      // Shift right to stay in viewport; if that covers the toggle, also shift down
      const coversToggle = PADDING + popperWidth > refRect.left;
      const x = Math.round(offsets.x + overflow);
      const y = Math.round(offsets.y + (coversToggle ? refRect.height : 0));
      state.styles.popper.transform = `translate3d(${x}px, ${y}px, 0)`;
      reference.classList.toggle(BELOW_CLASS, coversToggle);
    } else {
      reference.classList.remove(BELOW_CLASS);
    }
  };

  #toggle = (e) => {
    e.preventDefault();
    this.dropdown?.toggle();
  };

  #clearBelowClass = () => this.toggleTarget.classList.remove(BELOW_CLASS);

  #hideIfOther = (e) => {
    if (e.target !== this.toggleTarget) this.dropdown?.hide();
  };
}
